import { allServices as serviceCatalog, featuredServices, services as pillarServices } from "@/lib/data/services";

export { featuredServices, pillarServices };
export type { Service, ServiceFaq } from "@/lib/data/services";
/** Full catalog including specialty SEO landings */
export const services = serviceCatalog;

export const siteConfig = {
  name: "Maxridge",
  legalName: "Maxridge Private Limited",
  tagline: "AI-first software engineering & digital transformation partner.",
  description:
    "MaxRidge is an AI and software development company in India delivering Generative AI, AI agents, custom software, cloud, data engineering and dedicated development teams from Noida for clients worldwide.",
  url: "https://maxridge.ai",
  email: "sales@maxridge.ai",
  jobsEmail: "jobs@maxridge.ai",
  phone: "+91-81122-61134",
  phoneTel: "+918112261134",
  logo: {
    /** Prefer square PNG for Google Organization logo guidelines (≥112×112). */
    url: "https://maxridge.ai/logos/maxridge-apple-icon.png",
    width: 180,
    height: 180,
    wordmark: "https://maxridge.ai/logos/maxridge-logo.png",
    wordmarkDark: "https://maxridge.ai/logos/maxridge-logo-dark.png",
  },
  address: {
    building: "Devsha Business Park",
    line1: "D-215, D Block, Sector 63",
    line2: "Noida, Uttar Pradesh, 201301",
    country: "India",
    full: "Devsha Business Park, D-215, D Block, Sector 63, Noida, Uttar Pradesh, 201301",
    locality: "Noida",
    region: "Uttar Pradesh",
    postalCode: "201301",
    geo: { latitude: 28.6272, longitude: 77.375 },
  },
  maps: {
    embedUrl:
      "https://www.google.com/maps?q=Devsha+Business+Park,+D-215,+D+Block,+Sector+63,+Noida,+Uttar+Pradesh,+201301&output=embed",
    directionsUrl:
      "https://www.google.com/maps/search/?api=1&query=Devsha+Business+Park%2C+D-215%2C+D+Block%2C+Sector+63%2C+Noida%2C+Uttar+Pradesh%2C+201301",
  },
  officeImages: [
    {
      src: "/images/office/devsha-exterior.jpg",
      alt: "Devsha Business Park exterior — D-215, Sector 63, Noida",
    },
    {
      src: "/images/office/devsha-lobby.jpg",
      alt: "Devsha Business Park lobby and reception — Sector 63, Noida",
    },
  ],
  social: {
    linkedin: "https://www.linkedin.com/company/maxridge-private-limited/",
    twitter: "https://x.com/maxridge",
    github: "https://github.com/maxridge",
  },
  /** Primary SEO keywords aligned to software engineering / AI outsourcing demand */
  keywords: [
    "custom software development company",
    "software development company India",
    "software development company Noida",
    "software development company near me",
    "top software development companies",
    "best software development company India",
    "AI software development company",
    "generative AI development company",
    "machine learning development company",
    "AI agent development company",
    "LLM application development",
    "RAG development company",
    "enterprise AI development company",
    "hire software developers",
    "hire dedicated developers",
    "dedicated development team India",
    "offshore software development company",
    "nearshore software development",
    "IT outsourcing company India",
    "outsource software development to India",
    "web application development company",
    "mobile app development company",
    "SaaS product development company",
    "MVP development company",
    "startup software development company",
    "enterprise software development",
    "digital transformation services",
    "IT consulting company",
    "cloud migration services",
    "DevOps consulting services",
    "data engineering services",
    "system integration services",
    "application modernization services",
    "full stack development company",
    "React Next.js development company",
    "Python AI development company",
    "AWS Azure Google Cloud development",
    "AI consulting services",
    "business process automation company",
    "hire React developers India",
    "hire Python developers India",
    "hire AI engineers",
    "software product engineering",
    "AI-augmented software development",
    "fintech software development company",
    "healthcare software development company",
    "ecommerce software development company",
    "Maxridge",
    "Maxridge Private Limited",
    "Maxridge Noida",
    "Maxridge AI",
  ],
};

export const navLinks = [
  {
    label: "AI",
    href: "/services/artificial-intelligence",
  },
  {
    label: "Services",
    href: "/services",
    mega: "services" as const,
  },
  {
    label: "Solutions",
    href: "/solutions",
    mega: "solutions" as const,
  },
  {
    label: "Industries",
    href: "/industries",
    mega: "industries" as const,
  },
  {
    label: "Technology",
    href: "/technology",
    mega: "technology" as const,
  },
  {
    label: "Insights",
    href: "/insights",
  },
  {
    label: "About",
    href: "/about",
  },
  {
    label: "Career",
    href: "/careers",
  },
];

export const aiSolutions = [
  {
    title: "AI Copilots",
    description: "Assist employees with intelligent knowledge access across tools and workflows.",
  },
  {
    title: "AI Agents",
    description: "Automate multi-step business processes with orchestrated, tool-using agents.",
  },
  {
    title: "Enterprise RAG",
    description: "Turn enterprise knowledge into grounded, auditable answers.",
  },
  {
    title: "Intelligent Automation",
    description: "Automate repetitive workflows with AI-aware process design.",
  },
  {
    title: "Predictive Intelligence",
    description: "Convert business data into forecasts, signals, and decisions.",
  },
  {
    title: "AI-Powered CX",
    description: "Build intelligent customer interactions that feel human and precise.",
  },
] as const;

export const agenticWorkflow = [
  "User Request",
  "AI Orchestrator",
  "AI Agent",
  "Tools",
  "Enterprise Data",
  "Business Systems",
  "Decision",
  "Action",
] as const;

export const technologyGroups = [
  {
    category: "AI / ML",
    items: ["OpenAI", "Anthropic", "Gemini", "Llama", "LangChain", "LangGraph", "Hugging Face"],
  },
  {
    category: "Development",
    items: ["React", "Next.js", "Node.js", "Python", "Java", "Spring Boot", "FastAPI"],
  },
  {
    category: "Data",
    items: ["PostgreSQL", "MongoDB", "Snowflake", "Databricks", "Kafka", "Spark"],
  },
  {
    category: "Cloud",
    items: ["AWS", "Azure", "Google Cloud"],
  },
  {
    category: "DevOps",
    items: ["Docker", "Kubernetes", "Terraform", "GitHub Actions", "ArgoCD"],
  },
] as const;

export const industries = [
  {
    name: "Financial Services",
    slug: "financial-services",
    description: "Risk, compliance, and customer intelligence engineered for regulated environments.",
    visual: "finance",
  },
  {
    name: "Healthcare",
    slug: "healthcare",
    description: "Secure clinical and operational systems with responsible AI.",
    visual: "healthcare",
  },
  {
    name: "Retail & E-commerce",
    slug: "retail",
    description: "Personalization, inventory intelligence, and omnichannel experiences.",
    visual: "retail",
  },
  {
    name: "Manufacturing",
    slug: "manufacturing",
    description: "Digital twins, predictive maintenance, and plant-floor intelligence.",
    visual: "manufacturing",
  },
  {
    name: "Technology",
    slug: "technology",
    description: "Product engineering, platform modernization, and AI-native SaaS.",
    visual: "technology",
  },
  {
    name: "Logistics",
    slug: "logistics",
    description: "Connected supply chains with real-time decision systems.",
    visual: "logistics",
  },
  {
    name: "Education",
    slug: "education",
    description: "Adaptive learning platforms and institutional modernization.",
    visual: "education",
  },
  {
    name: "Public Sector",
    slug: "public-sector",
    description: "Secure digital services designed for citizens and scale.",
    visual: "public",
  },
  {
    name: "Telecommunications",
    slug: "telecommunications",
    description: "Network intelligence, customer systems, and automation at scale.",
    visual: "telecom",
  },
] as const;

export const caseStudies = [
  {
    id: "cs-01",
    slug: "knowledge-copilots-regulated-operations",
    industry: "Financial Services",
    client: "Confidential Enterprise Client",
    title: "Knowledge copilots for regulated operations",
    challenge:
      "Teams spent hours locating policies, procedures, and product knowledge across fragmented systems.",
    solution:
      "Built a governed enterprise RAG platform with role-aware retrieval and audit-ready responses.",
    technology: ["RAG", "LLM Apps", "Vector Search", "Azure"],
    impact: "Faster knowledge access and reduced operational friction across support teams.",
    href: "/case-studies/knowledge-copilots-regulated-operations",
    relatedServices: [
      "/services/rag-development",
      "/services/generative-ai-development",
      "/services/azure-consulting",
    ],
  },
  {
    id: "cs-02",
    slug: "plant-data-predictive-decisions",
    industry: "Manufacturing",
    client: "Confidential Enterprise Client",
    title: "From plant data to predictive decisions",
    challenge:
      "Critical operational signals were trapped in siloed systems with delayed reporting.",
    solution:
      "Designed a real-time data pipeline and predictive intelligence layer for production operations.",
    technology: ["Kafka", "Spark", "ML", "Cloud"],
    impact: "Earlier visibility into operational risk and more confident planning decisions.",
    href: "/case-studies/plant-data-predictive-decisions",
    relatedServices: [
      "/services/data-engineering",
      "/services/machine-learning-development",
      "/services/cloud-devops",
    ],
  },
  {
    id: "cs-03",
    slug: "intelligent-customer-experience",
    industry: "Retail & E-commerce",
    client: "Confidential Enterprise Client",
    title: "Intelligent customer experience orchestration",
    challenge:
      "Customer journeys were fragmented across channels with limited personalization intelligence.",
    solution:
      "Implemented an AI-assisted experience layer connected to CRM, catalog, and support systems.",
    technology: ["AI Agents", "APIs", "CRM", "Next.js"],
    impact: "More coherent customer interactions and improved decision support for service teams.",
    href: "/case-studies/intelligent-customer-experience",
    relatedServices: [
      "/services/ai-agent-development",
      "/services/api-development",
      "/services/web-app-development",
    ],
  },
] as const;

export const outcomes = [
  {
    title: "Faster time to market",
    description: "Compress discovery-to-production cycles with focused engineering and AI acceleration.",
  },
  {
    title: "Lower operational cost",
    description: "Automate repetitive work and reduce technology debt through deliberate modernization.",
  },
  {
    title: "Improved productivity",
    description: "Give teams copilots, agents, and systems that amplify expert judgment.",
  },
  {
    title: "Automated workflows",
    description: "Orchestrate multi-step processes across tools, data, and business systems.",
  },
  {
    title: "Better customer experiences",
    description: "Design intelligent interactions grounded in enterprise context.",
  },
  {
    title: "Scalable foundations",
    description: "Build cloud, data, and software platforms ready for continuous evolution.",
  },
] as const;

export const whyUs = [
  {
    title: "AI-First Thinking",
    description: "Technology designed around intelligent automation from day one.",
  },
  {
    title: "Engineering Depth",
    description: "Strong software, cloud, and data engineering capabilities.",
  },
  {
    title: "Business Outcomes",
    description: "Technology aligned with measurable business objectives.",
  },
  {
    title: "Speed & Agility",
    description: "Move from concept to production with disciplined velocity.",
  },
  {
    title: "Long-Term Partnership",
    description: "Build, modernize, optimize, and continuously evolve.",
  },
] as const;

export const deliverySteps = [
  { step: "01", title: "Discover", description: "Clarify goals, constraints, and opportunity." },
  { step: "02", title: "Strategize", description: "Define architecture, roadmap, and success measures." },
  { step: "03", title: "Design", description: "Shape experience, systems, and AI interaction models." },
  { step: "04", title: "Build", description: "Engineer production-ready platforms and products." },
  { step: "05", title: "Deploy", description: "Ship securely with observability and governance." },
  { step: "06", title: "Scale", description: "Expand reliably across teams, markets, and workloads." },
  { step: "07", title: "Optimize", description: "Improve performance, cost, and intelligence continuously." },
] as const;

export const maturityLevels = [
  {
    level: "01",
    title: "Digital Foundation",
    characteristics: "Core digital systems exist, but intelligence is limited and fragmented.",
    requirements: "Modern applications, cloud readiness, basic integrations.",
    challenges: "Legacy complexity, inconsistent data, slow delivery cycles.",
    next: "Establish trusted data foundations and modernization priorities.",
  },
  {
    level: "02",
    title: "Data Ready",
    characteristics: "Reliable data pipelines and governance enable analytics and insight.",
    requirements: "Warehouses/lakes, quality controls, BI and operational reporting.",
    challenges: "Silos, latency, weak ownership of data products.",
    next: "Introduce applied AI use cases with measurable business value.",
  },
  {
    level: "03",
    title: "AI Enabled",
    characteristics: "Select AI capabilities are embedded into workflows and products.",
    requirements: "Model integration, RAG, MLOps, responsible AI practices.",
    challenges: "Pilot sprawl, weak evaluation, limited production hardening.",
    next: "Industrialize AI platforms and expand high-ROI automation.",
  },
  {
    level: "04",
    title: "AI Powered",
    characteristics: "AI becomes a core operating layer across decisions and experiences.",
    requirements: "Enterprise AI platform, observability, governed multi-model strategy.",
    challenges: "Change management, cost control, cross-system orchestration.",
    next: "Introduce agentic systems that act across tools and data.",
  },
  {
    level: "05",
    title: "Agentic Enterprise",
    characteristics: "Orchestrated agents collaborate with humans across business systems.",
    requirements: "Agent frameworks, tool governance, secure system access, auditability.",
    challenges: "Trust, control planes, complex multi-agent coordination.",
    next: "Continuously refine agent ecosystems and outcome metrics.",
  },
] as const;

export const insights = [
  {
    category: "Agentic AI",
    title: "From copilots to agents: designing systems that act",
    type: "Perspective",
    readTime: "8 min",
    featured: true,
  },
  {
    category: "AI",
    title: "Enterprise RAG that leadership can trust",
    type: "Article",
    readTime: "6 min",
    featured: false,
  },
  {
    category: "Data",
    title: "Building AI-ready data platforms without the hype",
    type: "Research",
    readTime: "10 min",
    featured: false,
  },
  {
    category: "Cloud",
    title: "Cloud modernization patterns for intelligent systems",
    type: "Whitepaper",
    readTime: "12 min",
    featured: false,
  },
  {
    category: "Digital Transformation",
    title: "What CTOs should measure in AI transformation",
    type: "Perspective",
    readTime: "7 min",
    featured: false,
  },
] as const;

export const careerValues = [
  {
    title: "AI-first craft",
    description: "Work on systems where intelligence is a product capability, not a slide.",
  },
  {
    title: "Engineering seriousness",
    description: "Ship production-grade software with clarity, ownership, and high standards.",
  },
  {
    title: "Room to grow",
    description: "Stretch across AI, cloud, data, and product — with mentorship that matters.",
  },
] as const;

export const careerRoles = [
  {
    title: "AI Engineer",
    type: "Full-time · Remote / Hybrid",
    summary: "Design and productionize LLM applications, agents, and evaluation systems.",
  },
  {
    title: "Data Platform Engineer",
    type: "Full-time · Remote / Hybrid",
    summary: "Build AI-ready pipelines, lakehouse foundations, and governed analytics systems.",
  },
  {
    title: "Cloud / DevOps Engineer",
    type: "Full-time · Remote / Hybrid",
    summary: "Create secure cloud platforms, delivery pipelines, and observability foundations.",
  },
  {
    title: "Product Engineer",
    type: "Full-time · Remote / Hybrid",
    summary: "Ship premium enterprise interfaces and platforms with exceptional craft.",
  },
] as const;

export const trustPartners = [
  "AWS",
  "Azure",
  "Google Cloud",
  "OpenAI",
  "Anthropic",
  "Snowflake",
  "Databricks",
  "Kubernetes",
] as const;

export const evolutionStages = [
  {
    title: "Traditional Software",
    summary: "Systems that record work — slow cycles, limited intelligence.",
  },
  {
    title: "Cloud-Native Systems",
    summary: "Elastic platforms ready to scale products and teams.",
  },
  {
    title: "Data-Driven Organizations",
    summary: "Trusted data foundations that inform every decision.",
  },
  {
    title: "AI-Powered Organizations",
    summary: "Models embedded into products, workflows, and CX.",
  },
  {
    title: "Agentic Enterprises",
    summary: "Orchestrated agents that act across tools and systems.",
  },
] as const;

/** Homepage challenge → solution cards (conversion + SEO) */
export const challengeScenarios = [
  {
    title: "Accelerate your software roadmap",
    problem: "Deadlines are fixed, but delivery velocity is slipping.",
    helps: ["Dedicated engineering capacity", "Predictable sprint delivery", "Rapid MVP and feature shipping"],
    href: "/services/enterprise-software",
  },
  {
    title: "Ship AI into production",
    problem: "You need LLM products, agents, and ML systems that work beyond demos.",
    helps: ["Generative AI applications", "AI agent & workflow automation", "RAG and ML engineering"],
    href: "/services/artificial-intelligence",
  },
  {
    title: "Modernize legacy systems",
    problem: "Older platforms block growth, integrations, and cloud adoption.",
    helps: ["Application modernization", "Cloud migration", "Zero-downtime incremental upgrades"],
    href: "/services/cloud-devops",
  },
  {
    title: "Build a trusted data foundation",
    problem: "Decisions stall because data is siloed, late, or ungoverned.",
    helps: ["Data pipelines & lakehouses", "Analytics platforms", "AI-ready data architecture"],
    href: "/services/data-engineering",
  },
  {
    title: "Scale with a dedicated team",
    problem: "Hiring is slow and you need senior engineers who own outcomes.",
    helps: ["Dedicated development teams", "Full-stack product squads", "Flexible engagement models"],
    href: "/services/hire-dedicated-developers",
  },
  {
    title: "Transform with clear IT strategy",
    problem: "Technology choices feel fragmented without an executive-ready roadmap.",
    helps: ["Digital transformation consulting", "Solution architecture", "AI strategy & governance"],
    href: "/services/it-consulting",
  },
] as const;

export const homeFaqs = [
  {
    question: "What services does Maxridge offer as a software development company?",
    answer:
      "Maxridge delivers custom software development, web and mobile apps, MVP development, generative AI and machine learning, data engineering, cloud & DevOps, system integration, dedicated development teams, IT consulting, and digital transformation.",
  },
  {
    question: "Can we hire dedicated developers or a full engineering team?",
    answer:
      "Yes. You can hire dedicated developers or managed squads for React, Python, AI, cloud, and full-stack work — with clear ownership, sprint cadence, and production-quality standards.",
  },
  {
    question: "Do you build production AI systems, not just prototypes?",
    answer:
      "Yes. We engineer LLM applications, RAG systems, AI agents, ML pipelines, and governed AI platforms designed for reliability, security, evaluation, and measurable business outcomes.",
  },
  {
    question: "Is Maxridge a good alternative for offshore software development in India?",
    answer:
      "Maxridge is based in Sector 63, Noida (Delhi NCR) and partners with startups and enterprises worldwide for AI-first product engineering, dedicated teams, and digital transformation with transparent delivery.",
  },
  {
    question: "Which industries does Maxridge support?",
    answer:
      "We partner across financial services, healthcare, retail & e-commerce, manufacturing, technology/SaaS, logistics, education, public sector, and telecommunications.",
  },
  {
    question: "How do we get started with Maxridge?",
    answer:
      "Share your goals, constraints, and timeline via our contact form or email sales@maxridge.ai. We respond with a clear discovery plan — typically within one business day.",
  },
] as const;